/**
 * Remote Browser Client
 *
 * Socket.IO client for connecting to the Remote Browser Service.
 * Handles WebSocket connection, event handling, and reconnection.
 */

import { io, Socket } from 'socket.io-client';
import { logger } from '@shared/utils/logger';

// ============================================================================
// Types
// ============================================================================

/** Client → Server events */
export const ClientEvents = {
  BROWSER_OPEN: 'browser:open',
  BROWSER_NAVIGATE: 'browser:navigate',
  BROWSER_CLOSE: 'browser:close',
  MOUSE_MOVE: 'browser:mouse:move',
  MOUSE_CLICK: 'browser:mouse:click',
  MOUSE_SCROLL: 'browser:mouse:scroll',
  KEY_DOWN: 'browser:key:down',
  KEY_UP: 'browser:key:up',
  TYPE: 'browser:type',
  STREAM_START: 'browser:stream:start',
  STREAM_STOP: 'browser:stream:stop',
  // Viewport resize - sent when client panel size changes
  // Tells server to call page.setViewportSize() to match client display
  VIEWPORT_RESIZE: 'browser:viewport:resize',
  PING: 'ping',
  // Clipboard and context menu operations
  GET_SELECTION: 'browser:clipboard:getSelection',
  PASTE: 'browser:clipboard:paste',
  RELOAD: 'browser:reload',
  SELECT_ALL: 'browser:selectAll',
} as const;

/** Server → Client events */
export const ServerEvents = {
  BROWSER_OPENED: 'browser:opened',
  BROWSER_NAVIGATED: 'browser:navigated',
  BROWSER_CLOSED: 'browser:closed',
  BROWSER_ERROR: 'browser:error',
  LOADING: 'browser:loading',
  TITLE: 'browser:title',
  FRAME: 'browser:frame',
  PONG: 'pong',
  CONNECTED: 'browser:connected',
  // Clipboard result event
  SELECTION_RESULT: 'browser:clipboard:selectionResult',
} as const;

/** Browser open payload */
export interface BrowserOpenPayload {
  url: string;
  profileId?: string;
  viewport?: { width: number; height: number };
}

/** Browser opened response */
export interface BrowserOpenedResponse {
  pageId: string;
  url: string;
  title: string;
  width: number;
  height: number;
  profileId: string;
}

/** Browser navigated response */
export interface BrowserNavigatedResponse {
  pageId: string;
  url: string;
  title: string;
}

/** Browser error response */
export interface BrowserErrorResponse {
  pageId?: string;
  error: string;
  code?: string;
}

/** Frame payload */
export interface FramePayload {
  pageId: string;
  image: string; // Base64 JPEG
  timestamp: number;
  width: number;
  height: number;
}

/** Loading state payload */
export interface LoadingPayload {
  pageId: string;
  isLoading: boolean;
}

/** Title update payload */
export interface TitlePayload {
  pageId: string;
  title: string;
}

/** Selection result payload (from getSelection) */
export interface SelectionResultPayload {
  pageId: string;
  text: string;
  success: boolean;
}

/** Connection state */
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** Event listener types */
export interface RemoteBrowserEventHandlers {
  onOpened?: (data: BrowserOpenedResponse) => void;
  onNavigated?: (data: BrowserNavigatedResponse) => void;
  onClosed?: (data: { pageId: string; reason?: string }) => void;
  onError?: (data: BrowserErrorResponse) => void;
  onFrame?: (data: FramePayload) => void;
  onLoading?: (data: LoadingPayload) => void;
  onTitle?: (data: TitlePayload) => void;
  onConnectionChange?: (state: ConnectionState) => void;
}

// ============================================================================
// Client Class
// ============================================================================

/**
 * Remote Browser Client
 */
export class RemoteBrowserClient {
  private socket: Socket | null = null;
  private handlers: RemoteBrowserEventHandlers = {};
  private connectionState: ConnectionState = 'disconnected';
  private serverUrl: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private frameDebugCount = 0;

  constructor(serverUrl?: string) {
    // Default to production URL, can be overridden
    this.serverUrl = serverUrl || this.getDefaultServerUrl();
  }

  /**
   * Get default server URL based on environment
   */
  private getDefaultServerUrl(): string {
    // Check environment variable first (highest priority)
    const envUrl = import.meta.env.VITE_REMOTE_BROWSER_URL;
    if (envUrl) {
      return envUrl;
    }

    // Check if we're accessing via ngrok (treat as production even in dev mode)
    const isNgrok = typeof window !== 'undefined' && window.location.hostname.includes('ngrok');

    // In development (but not ngrok), connect to local server
    if (import.meta.env.DEV && !isNgrok) {
      return 'http://localhost:3001';
    }
    // Production or ngrok: Use vibe-remote subdomain (base URL only, path configured separately)
    return 'https://tools.eroland.me';
  }

  /**
   * Get socket.io path based on environment
   */
  private getSocketPath(): string {
    // Browser handlers are now integrated into the main vibe-remote server
    // Always use the default socket.io path since browser handlers share the same server
    return '/socket.io';
  }

  /**
   * Connect to the remote browser service
   */
  connect(handlers?: RemoteBrowserEventHandlers): void {
    if (this.socket?.connected) {
      logger.debug('[RemoteBrowserClient] Already connected');
      return;
    }

    if (handlers) {
      this.handlers = handlers;
    }

    this.setConnectionState('connecting');
    logger.info('[RemoteBrowserClient] Connecting to:', this.serverUrl);

    try {
      // Log for debugging - always visible even in production
      // eslint-disable-next-line no-console
      console.log('[RemoteBrowserClient] Creating socket connection to:', this.serverUrl);

      this.socket = io(this.serverUrl, {
        path: this.getSocketPath(),
        transports: ['websocket'], // WebSocket only for lower latency (no polling fallback)
        timeout: 20000,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        // Performance optimizations
        upgrade: false, // Skip upgrade from polling since we start with websocket
        forceNew: false, // Reuse existing connection
      });

      // eslint-disable-next-line no-console
      console.log('[RemoteBrowserClient] Socket created:', !!this.socket);

      this.setupEventListeners();
    } catch (err) {
      // Always log errors in production for debugging
      // eslint-disable-next-line no-console
      console.error('[RemoteBrowserClient] Failed to create socket:', err);
      this.setConnectionState('disconnected');
      this.handlers.onError?.({ error: err instanceof Error ? err.message : 'Failed to create socket connection' });
    }
  }

  /**
   * Disconnect from the service
   */
  disconnect(): void {
    if (this.socket) {
      logger.info('[RemoteBrowserClient] Disconnecting');
      this.socket.disconnect();
      this.socket = null;
    }
    this.setConnectionState('disconnected');
  }

  /**
   * Set connection state and notify handlers
   */
  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.handlers.onConnectionChange?.(state);
  }

  /**
   * Setup Socket.IO event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      // eslint-disable-next-line no-console
      console.log('[RemoteBrowserClient] Connected successfully');
      logger.info('[RemoteBrowserClient] Connected');
      this.reconnectAttempts = 0;
      this.setConnectionState('connected');
    });

    this.socket.on('disconnect', (reason) => {
      // eslint-disable-next-line no-console
      console.log('[RemoteBrowserClient] Disconnected:', reason);
      logger.info('[RemoteBrowserClient] Disconnected:', reason);
      this.setConnectionState('disconnected');
    });

    this.socket.on('connect_error', (error) => {
      // eslint-disable-next-line no-console
      console.error('[RemoteBrowserClient] Connection error:', error.message, error);
      logger.error('[RemoteBrowserClient] Connection error:', error.message);
      this.reconnectAttempts++;
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.setConnectionState('reconnecting');
      } else {
        this.setConnectionState('disconnected');
      }
    });

    // Browser events
    this.socket.on(ServerEvents.BROWSER_OPENED, (data: BrowserOpenedResponse) => {
      logger.debug('[RemoteBrowserClient] Browser opened:', data.pageId);
      this.handlers.onOpened?.(data);
    });

    this.socket.on(ServerEvents.BROWSER_NAVIGATED, (data: BrowserNavigatedResponse) => {
      logger.debug('[RemoteBrowserClient] Browser navigated:', data.url);
      this.handlers.onNavigated?.(data);
    });

    this.socket.on(ServerEvents.BROWSER_CLOSED, (data: { pageId: string; reason?: string }) => {
      logger.debug('[RemoteBrowserClient] Browser closed:', data.pageId);
      this.handlers.onClosed?.(data);
    });

    this.socket.on(ServerEvents.BROWSER_ERROR, (data: BrowserErrorResponse) => {
      logger.error('[RemoteBrowserClient] Browser error:', data.error);
      this.handlers.onError?.(data);
    });

    this.socket.on(ServerEvents.FRAME, (data: FramePayload) => {
      // Log first frame and then every 15th frame for debugging
      this.frameDebugCount++;
      if (this.frameDebugCount === 1 || this.frameDebugCount % 15 === 0) {
        // eslint-disable-next-line no-console
        console.log(`[RemoteBrowserClient] Frame ${this.frameDebugCount} received, pageId: ${data.pageId}, size: ${data.image?.length || 0}`);
      }
      this.handlers.onFrame?.(data);
    });

    this.socket.on(ServerEvents.LOADING, (data: LoadingPayload) => {
      logger.debug('[RemoteBrowserClient] Loading:', data.isLoading);
      this.handlers.onLoading?.(data);
    });

    this.socket.on(ServerEvents.TITLE, (data: TitlePayload) => {
      logger.debug('[RemoteBrowserClient] Title:', data.title);
      this.handlers.onTitle?.(data);
    });

    this.socket.on(ServerEvents.CONNECTED, (data: { clientId: string }) => {
      logger.info('[RemoteBrowserClient] Server assigned client ID:', data.clientId);
    });
  }

  // ==========================================================================
  // Browser Control Methods
  // ==========================================================================

  /**
   * Open a new browser page
   */
  openBrowser(payload: BrowserOpenPayload): Promise<BrowserOpenedResponse> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Not connected to remote browser service'));
        return;
      }

      this.socket.emit(
        ClientEvents.BROWSER_OPEN,
        payload,
        (response: BrowserOpenedResponse | BrowserErrorResponse) => {
          if ('error' in response) {
            reject(new Error(response.error));
          } else {
            resolve(response);
          }
        }
      );
    });
  }

  /**
   * Navigate to a URL
   */
  navigate(pageId: string, url: string): Promise<BrowserNavigatedResponse> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Not connected to remote browser service'));
        return;
      }

      this.socket.emit(
        ClientEvents.BROWSER_NAVIGATE,
        { pageId, url },
        (response: BrowserNavigatedResponse | BrowserErrorResponse) => {
          if ('error' in response) {
            reject(new Error(response.error));
          } else {
            resolve(response);
          }
        }
      );
    });
  }

  /**
   * Close a browser page
   */
  closeBrowser(pageId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.BROWSER_CLOSE, { pageId });
  }

  /**
   * Start screenshot streaming
   */
  startStreaming(pageId: string, options?: { fps?: number; quality?: number }): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.STREAM_START, { pageId, ...options });
  }

  /**
   * Stop screenshot streaming
   */
  stopStreaming(pageId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.STREAM_STOP, { pageId });
  }

  // ==========================================================================
  // Input Methods
  // ==========================================================================

  /**
   * Send mouse move event
   */
  mouseMove(pageId: string, x: number, y: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.MOUSE_MOVE, { pageId, x, y });
  }

  /**
   * Send mouse click event
   */
  mouseClick(
    pageId: string,
    x: number,
    y: number,
    button: 'left' | 'right' | 'middle' = 'left',
    clickCount = 1
  ): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.MOUSE_CLICK, { pageId, x, y, button, clickCount });
  }

  /**
   * Send scroll event
   */
  mouseScroll(pageId: string, deltaX: number, deltaY: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.MOUSE_SCROLL, { pageId, deltaX, deltaY });
  }

  /**
   * Send key down event
   */
  keyDown(pageId: string, key: string, modifiers?: ('Alt' | 'Control' | 'Meta' | 'Shift')[]): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.KEY_DOWN, { pageId, key, modifiers });
  }

  /**
   * Send key up event
   */
  keyUp(pageId: string, key: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.KEY_UP, { pageId, key });
  }

  /**
   * Type text
   */
  type(pageId: string, text: string, delay?: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.TYPE, { pageId, text, delay });
  }

  /**
   * Resize viewport - call when client panel size changes.
   * This triggers server-side page.setViewportSize() so Playwright screenshots
   * match the client display size exactly, preventing stretching/squishing.
   * Width/height should be logical pixels (not physical pixels with DPR applied).
   */
  resizeViewport(
    pageId: string,
    width: number,
    height: number,
    devicePixelRatio?: number
  ): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.VIEWPORT_RESIZE, {
      pageId,
      width,
      height,
      devicePixelRatio,
    });
  }

  // ==========================================================================
  // Clipboard & Context Menu Methods
  // ==========================================================================

  /**
   * Get selected text from the remote browser page.
   * Returns the currently selected text via Promise (uses callback-based emit).
   */
  getSelection(pageId: string): Promise<SelectionResultPayload> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Not connected to remote browser service'));
        return;
      }

      this.socket.emit(
        ClientEvents.GET_SELECTION,
        { pageId },
        (response: SelectionResultPayload | BrowserErrorResponse) => {
          if ('error' in response) {
            reject(new Error(response.error));
          } else {
            resolve(response);
          }
        }
      );
    });
  }

  /**
   * Paste text into the remote browser at current cursor position.
   * Simulates Ctrl+V with the provided text content.
   */
  paste(pageId: string, text: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.PASTE, { pageId, text });
  }

  /**
   * Reload the current page in the remote browser.
   */
  reload(pageId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.RELOAD, { pageId });
  }

  /**
   * Select all content on the current page (Ctrl+A equivalent).
   */
  selectAll(pageId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ClientEvents.SELECT_ALL, { pageId });
  }

  // ==========================================================================
  // Getters
  // ==========================================================================

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  /**
   * Update event handlers
   */
  setHandlers(handlers: RemoteBrowserEventHandlers): void {
    this.handlers = { ...this.handlers, ...handlers };
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let clientInstance: RemoteBrowserClient | null = null;

/**
 * Get or create the singleton client instance
 */
export function getRemoteBrowserClient(): RemoteBrowserClient {
  if (!clientInstance) {
    clientInstance = new RemoteBrowserClient();
  }
  return clientInstance;
}

/**
 * Reset client instance (for testing)
 */
export function resetRemoteBrowserClient(): void {
  if (clientInstance) {
    clientInstance.disconnect();
    clientInstance = null;
  }
}
